import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, AlertTriangle, Home, UtensilsCrossed, Trees, Shield, Dog, Car, Volume2, LogOut } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import type { StructuredHouseRules, HouseRuleCategory } from '../../types';

interface HouseRulesDisplayProps {
  rules?: StructuredHouseRules;
}

const categoryIcons: Record<HouseRuleCategory, React.ReactNode> = {
  general: <Home className="h-4 w-4" />,
  kitchen: <UtensilsCrossed className="h-4 w-4" />,
  outdoor: <Trees className="h-4 w-4" />,
  safety: <Shield className="h-4 w-4" />,
  pets: <Dog className="h-4 w-4" />,
  parking: <Car className="h-4 w-4" />,
  noise: <Volume2 className="h-4 w-4" />,
  checkout: <LogOut className="h-4 w-4" />,
  custom: <AlertTriangle className="h-4 w-4" />,
};

const HouseRulesDisplay: React.FC<HouseRulesDisplayProps> = ({ rules }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Freetext format (new editor)
  if (rules?.freeText?.de) {
    return (
      <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
        {rules.freeText.de}
      </p>
    );
  }

  // Legacy structured format
  if (!rules || !rules.groups || rules.groups.length === 0) {
    return (
      <div className="text-muted-foreground text-center py-8">
        {t('common.noContent')}
      </div>
    );
  }

  const getCategoryName = (group: typeof rules.groups[0]) => {
    if (group.category === 'custom' && group.customCategoryName) {
      return group.customCategoryName[language];
    }
    return t(`houseRules.categories.${group.category}`);
  };

  return (
    <Accordion type="multiple" defaultValue={rules.groups.map(g => g.category)} className="w-full">
      {rules.groups.map((group) => (
        <AccordionItem key={group.category} value={group.category}>
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground">
                {categoryIcons[group.category]}
              </span>
              <span className="font-medium">{getCategoryName(group)}</span>
              <Badge variant="secondary" className="ml-2">
                {group.rules.length}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ul className="space-y-3 pl-2">
              {group.rules.map((rule) => (
                <li key={rule.id} className="flex items-start gap-3">
                  <Check className={`h-5 w-5 flex-shrink-0 mt-0.5 ${rule.isRequired ? 'text-red-500' : 'text-green-500'}`} />
                  <span className={rule.isRequired ? 'font-medium' : ''}>
                    {rule.text[language]}
                    {rule.isRequired && (
                      <Badge variant="destructive" className="ml-2 text-xs">
                        {t('houseRules.required')}
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default HouseRulesDisplay;
